
import { colors } from 'quasar'
import { Component, Mixins } from 'vue-property-decorator'

import FeedbackType from '@/shared/enums/FeedbackType'
import KeyCode from '@/shared/enums/KeyCode'
import MessageType from '@/shared/enums/MessageType'
import PaymentMode from '@/shared/enums/PaymentMode'
import Step from '@/shared/enums/Step'
import SubscriptionScheme from '@/shared/enums/SubscriptionScheme'

import PostMessageMixin, { Message } from '@/shared/mixins/post_message/PostMessageMixin'
import isMobile from '@/widget/utils/isMobile'

import CheckoutConfig from '@/shared/models/CheckoutConfig'
import BankSlip from './models/BankSlip'
import BankTransfer from './models/BankTransfer'
import Breb from './models/Breb'
import CheckoutForm from './models/CheckoutForm'
import Deuna from './models/Deuna'
import Mach from './models/Mach'
import Nequi from './models/Nequi'
import Oxxo from './models/Oxxo'
import PagoEfectivo from './models/PagoEfectivo'
import PayCash from './models/PayCash'
import PicPay from './models/PicPay'
import Pix from './models/Pix'
import PixAutomatico from './models/PixAutomatico'
import QrInteroperable from './models/QrInteroperable'
import Spei from './models/Spei'
import TiendasYFarmacias from './models/TiendasYFarmacias'

import { isCardMethod, methodLabel } from '@/shared/utils/paymentMethodHelpers'
import { loadScript } from './utils/helpers'
import mapError from './utils/mapError'
import { SizeObserver } from './utils/sizeObserver'

import checkoutService from './network/service/checkout.service'
import fraudProtectionService from './network/service/fraudProtection.service'

import { PaymentStatus } from '@/shared/enums/PaymentStatus'
import { SubscriptionStatus } from '@/shared/enums/SubscriptionStatus'
import EBankSlip from './components/BankSlip.vue'
import EBankTransfer from './components/BankTransfer.vue'
import EBreb from './components/Breb.vue'
import ECart from './components/Cart.vue'
import EDeuna from './components/Deuna.vue'
import EFeedback from './components/Feedback.vue'
import EFooter from './components/Footer.vue'
import EForm from './components/Form.vue'
import EHeader from './components/Header.vue'
import ELegacyLayout from './components/layouts/LegacyLayout.vue'
import EMobileLayout from './components/layouts/MobileLayout.vue'
import EPaymentLayout from './components/layouts/PaymentLayout.vue'
import ELoader from './components/Loader.vue'
import EMach from './components/Mach.vue'
import ENequi from './components/Nequi.vue'
import EOxxo from './components/Oxxo.vue'
import EPagoEfectivo from './components/PagoEfectivo.vue'
import EPanel from './components/Panel.vue'
import EPayCash from './components/PayCash.vue'
import EPaymentMethods from './components/PaymentMethods.vue'
import EPicPay from './components/PicPay.vue'
import EPix from './components/Pix.vue'
import EPixAutomatico from './components/PixAutomatico.vue'
import EQrInteroperable from './components/QrInteroperable.vue'
import ESpei from './components/Spei.vue'
import ETiendasYFarmacias from './components/TiendasYFarmacias.vue'

declare global {
  interface Window {
    bpmpi_load: Function
    bpmpi_authenticate: Function
  }
}

declare interface ThreeDSSetUpParams {
  env: string
  session: string
  contractId: string
}

declare interface Enroll3DSResponse {
  paymentToken: string
  id?: string
  publicPersonId?: string
  publicCardId?: string
  subscriptionId?: string
}

declare interface ThreeDSAuthenticateCallbacks {
  onSuccess: (data: Enroll3DSResponse) => void
  onUnenrolled: () => void
  onFailure: () => void
  onError: (e: any) => void
}

interface $ThreeDS {
  setUp(config: Partial<ThreeDSSetUpParams>): void
  authenticate3DS(request: any, callbacks: ThreeDSAuthenticateCallbacks): Promise<void>
}

declare let ThreeDS: $ThreeDS

// eslint-disable-next-line
declare let dftp: any

type RedirectType = 'success' | 'declined' | 'error' | 'legacy'

// The redirect a failed payment status leads to, if any.
const failureRedirect = (status?: PaymentStatus | null): RedirectType | null =>
  status === PaymentStatus.declined ? 'declined'
    : status === PaymentStatus.error ? 'error' : null

type Feedback = {
  type: FeedbackType
  title?: string
  message: string
  step?: number
  retry?: boolean
}

@Component({
  components: {
    EBankSlip,
    EFeedback,
    EFooter,
    EForm,
    EHeader,
    ELoader,
    EPix,
    EPixAutomatico,
    EOxxo,
    ETiendasYFarmacias,
    ESpei,
    EPayCash,
    EPicPay,
    EBankTransfer,
    EPagoEfectivo,
    EDeuna,
    ENequi,
    EMach,
    EQrInteroperable,
    EBreb,
    EPanel,
    EPaymentMethods,
    ECart,
    EPaymentLayout,
    ELegacyLayout,
    EMobileLayout,
  },
  computed: {
    PaymentMode: () => PaymentMode,
  }
})
export default class App extends Mixins(PostMessageMixin) {
  $refs!: {
    form: EForm
  }

  loadingMap: Record<string, boolean> = {}

  get isLoading(): boolean {
    if (this.isSubscription || !this.paymentMethod) {
      return this.loadingMap['checkout'] ?? false
    }

    return this.loadingMap[this.paymentMethod] ?? false
  }

  useExternal3ds = false

  returnUrl = ''

  successUrl = ''

  declinedUrl = ''

  errorUrl = ''

  baseConfig: CheckoutConfig | null = null

  availableMethods: PaymentMode[] = []

  bankSlip: BankSlip | null = null

  pix: Pix | null = null

  pixAutomatico: PixAutomatico | null = null

  oxxo: Oxxo | null = null

  paynet: TiendasYFarmacias | null = null

  tiendasYFarmacias: TiendasYFarmacias | null = null

  spei: Spei | null = null

  paycash: PayCash | null = null

  picpay: PicPay | null = null

  bankTransfer: BankTransfer | null = null

  pagoEfectivo: PagoEfectivo | null = null

  deuna: Deuna | null = null

  nequi: Nequi | null = null

  mach: Mach | null = null

  qrInteroperable: QrInteroperable | null = null

  breb: Breb | null = null

  feedbackMap: Record<string, Feedback | null> = {}

  // Set when the restore request fails: the session state is unknown, so the
  // payer is asked to reload instead of being offered a new payment.
  loadError: Feedback | null = null

  get feedback(): Feedback | null {
    if (this.loadError) {
      return this.loadError
    }

    if (this.isSubscription || !this.paymentMethod) {
      return this.feedbackMap['checkout'] ?? null
    }

    return this.feedbackMap[this.paymentMethod] ?? null
  }

  formData: CheckoutForm | null = null

  logoUrl: string | undefined = ''

  nethoneAttemptReference?: string

  processPayment = false
  delayCapture = false
  saveCard = true

  step = 0;
  paymentMethod: PaymentMode | null = null

  private sizeObserverInstance: SizeObserver | null = null

  private readonly isMobile = isMobile()

  get targetOrigin(): string | null {
    const searchParams = new URLSearchParams(window.location.search)
    return searchParams.get('url')
  }

  get targetWindow(): Window {
    return window.opener || window.parent
  }

  get transactionAmount(): number {
    return (this.formData && this.formData.payment.amount) || 0
  }

  get transactionCurrency(): string {
    return (this.formData && this.formData.payment.asset) || 'BRL'
  }

  get layout() {
    const hasMultiplePaymentMethods = this.availableMethods.length > 1;

    if (!hasMultiplePaymentMethods) {
      return ELegacyLayout;
    }

    return this.isMobile ? EMobileLayout : EPaymentLayout;
  }

  get pixDirect(): boolean {
    const pixDirect = this.baseConfig?.pixDirect ?? false
    const paymentMethodIsPix = this.paymentMethod === PaymentMode.pix
    return pixDirect && paymentMethodIsPix
  }

  get use3ds(): boolean {
    const use3ds = this.baseConfig?.lightboxUse3ds ?? false
    const isCard = Boolean(
      this.paymentMethod &&
      isCardMethod(this.paymentMethod)
    )

    return use3ds && (isCard || this.isSubscribingWithCard)
  }

  get isSubscription(): boolean {
    return this.baseConfig?.subscription ?? false
  }

  get isSubscribingWithCard(): boolean {
    return this.isSubscription && this.baseConfig?.subscriptionScheme === SubscriptionScheme.card
  }

  get hasMultiplePaymentMethods(): boolean {
    return this.availableMethods.length > 1
  }

  get paymentMethodLabel(): string {
    if (this.isSubscription && (this.feedback || this.isLoading)) {
      return '';
    }

    if (this.isSubscription) {
      return this.$tc('message.subscription')
    }

    if (!this.paymentMethod) {
      return ''
    }

    return methodLabel(this.paymentMethod)
  }

  private loadNethoneScript() {
    const nethoneScript = document.createElement('script')
    nethoneScript.setAttribute('src', process.env.VUE_APP_NETHONE_FINGERPRINTING_SRC)
    nethoneScript.setAttribute('async', 'async')
    nethoneScript.setAttribute('crossorigin', 'use-credentials')
    document.head.appendChild(nethoneScript)

    nethoneScript.addEventListener('load', async () => {
      const token = await fraudProtectionService.token()

      const options = {
        attemptReference: token.token,
        sensitiveFields: ['cpf', 'cardNumber', 'cvv'],
      }

      this.nethoneAttemptReference = token.token

      dftp.init(options)
    })
  }

  mounted() {
    document.addEventListener('keyup', this.escapeHandler)
    this.sendMessage(MessageType.ready)
    this.initializeSizeObserver()

    if (process.env.NODE_ENV !== 'development') {
      // loadNethoneScript();
    }
  }

  destroyed() {
    document.removeEventListener('keyup', this.escapeHandler)
    this.cleanupSizeObserver()
  }

  barcodeCopied() {
    this.sendMessage(MessageType.bankSlipCopy)
  }

  pixCodeCopied() {
    this.sendMessage(MessageType.pixCopy)
  }

  speiClabeCopied() {
    this.sendMessage(MessageType.speiCopy)
  }

  pagoEfectivoCipCodeCopied() {
    this.sendMessage(MessageType.pagoEfectivoCopy)
  }

  escapeHandler({ which }: UIEvent): void {
    if (which === KeyCode.esc) {
      this.close()
    }
  }

  close(): void {
    this.sendMessage(MessageType.closePayment)
  }

  setStep(step: number) {
    this.step = step
  }

  selectMethod(method: PaymentMode | null) {
    if (!this.baseConfig) {
      return
    }

    this.paymentMethod = method
    this.setFeedback(null, method)

    const configForMethod: CheckoutConfig = method
      ? { ...this.baseConfig, method }
      : this.baseConfig

    this.formData = new CheckoutForm(configForMethod)

    if (
      method === PaymentMode.pix &&
      this.pixDirect &&
      !this.pix
    ) {
      this.submitCheckout(this.formData)
    }

    // Trigger size update after form changes
    this.$nextTick(() => {
      if (this.sizeObserverInstance) {
        this.sizeObserverInstance.forceUpdate()
      }
    })
  }

  isResultVisibleFor(mode: PaymentMode | 'pixAutomatico'): boolean {
    if (this.isLoading) {
      return false
    }

    if (mode === 'pixAutomatico') {
      return !!this.pixAutomatico
    }

    const isMethodSelected = this.paymentMethod === mode
    const hasResult = ((): boolean => {
      switch (mode) {
        case PaymentMode.bankSlip: return !!this.bankSlip
        case PaymentMode.pix: return !!this.pix
        case PaymentMode.oxxo: return !!this.oxxo
        case PaymentMode.paynet: return !!this.paynet
        case PaymentMode.tiendasYFarmacias: return !!this.tiendasYFarmacias
        case PaymentMode.spei: return !!this.spei
        case PaymentMode.paycash: return !!this.paycash
        case PaymentMode.picpay: return !!this.picpay
        case PaymentMode.bankTransfer: return !!this.bankTransfer
        case PaymentMode.pagoEfectivo: return !!this.pagoEfectivo
        case PaymentMode.deuna: return !!this.deuna
        case PaymentMode.nequi: return !!this.nequi
        case PaymentMode.mach: return !!this.mach
        case PaymentMode.qrInteroperable: return !!this.qrInteroperable
        case PaymentMode.breb: return !!this.breb
        default: return false
      }
    })()

    return isMethodSelected && hasResult
  }

  async receiveMessage(message: Message): Promise<void> {
    if (message.type !== MessageType.initData) {
      return
    }

    const config = message.payload as CheckoutConfig

    this.baseConfig = config

    if (config.checkoutId) {
      this.setIsLoading(true)

      try {
        const response = await checkoutService.getMethods(config.checkoutId)

        Object.assign(this, {
          bankSlip: response.bankSlip ?? null,
          pix: response.pix ?? null,
          pixAutomatico: response.pixAutomatico ?? null,
          oxxo: response.oxxo ?? null,
          paynet: response.paynet ?? null,
          tiendasYFarmacias: response.tiendasYFarmacias ?? null,
          spei: response.spei ?? null,
          paycash: response.paycash ?? null,
          picpay: response.picpay ?? null,
          bankTransfer: response.bankTransfer ?? null,
          pagoEfectivo: response.pagoEfectivo ?? null,
          deuna: response.deuna ?? null,
          nequi: response.nequi ?? null,
          mach: response.mach ?? null,
          qrInteroperable: response.qrInteroperable ?? null,
          breb: response.breb ?? null,
        })
      } catch(e) {
        console.error(e);

        this.loadError = {
          type: FeedbackType.error,
          title: 'message.checkoutLoadErrorTitle',
          message: 'message.checkoutLoadError',
          retry: false,
        }
      } finally {
        this.setIsLoading(false)
      }
    }

    this.logoUrl = config.lightboxLogoUrl
    this.availableMethods = config.methods ?? []

    this.setBrandColors(config)
    this.setup3ds(config)

    const method = this.availableMethods.length <= 1 ? config.method : null
    this.selectMethod(method ?? null)

    this.processPayment = config.processPayment
    this.delayCapture = config.delayCapture
    this.saveCard = config.saveCard
    this.useExternal3ds = config.lightboxUseExternal3ds ?? false

    this.returnUrl = config.returnUrl ?? ''
    this.successUrl = config.successUrl?.trim() ?? ''
    this.declinedUrl = config.declinedUrl?.trim() ?? ''
    this.errorUrl = config.errorUrl?.trim() ?? ''

    if (!this.formData) {
      return
    }

    if (process.env.VUE_APP_GOOGLE_ANALYTICS_ID) {
      this.$gtag.event('load', this.formData.toAnalyticsEvent(config.lightboxUse3ds))
    }
  }

  setBrandColors(config: CheckoutConfig) {
    this.$q.addressbarColor.set(config.lightboxAddressBarColor ?? String())

    colors.setBrand('primary', config.lightboxPrimaryColor ?? String())
    colors.setBrand('secondary', config.lightboxSecondaryColor ?? String())
    colors.setBrand('accent', config.lightboxAccentColor ?? String())
    colors.setBrand('positive', config.lightboxPositiveColor ?? String())
    colors.setBrand('negative', config.lightboxNegativeColor ?? String())
    colors.setBrand('info', config.lightboxInfoColor ?? String())
    colors.setBrand('warning', config.lightboxWarningColor ?? String())

    const { r, g, b } = colors.hexToRgb(config.lightboxAccentColor ?? String())
    document.documentElement.style.setProperty(
      '--q-color-accent-rgb',
      `${r}, ${g}, ${b}`,
    )
  }

  async submitCheckout(data: CheckoutForm) {
    this.setIsLoading(true, data.payment.method)

    if (process.env.VUE_APP_GOOGLE_ANALYTICS_ID) {
      this.$gtag.event('pay', data.toAnalyticsEvent(false))
    }

    // if (process.env.NODE_ENV !== 'development' && window.dftp) {
    //   try {
    //     await dftp.profileCompleted();
    //   } catch (e) {
    //     console.error(`Profiling failed with error: ${e}`);
    //   } finally {
    //     await this.submitCheckoutForm(data);
    //   }
    // } else {
    await this.submitCheckoutForm(data)
    // }
  }

  // Where redirect(type) sends the payer, or null when they stay on the checkout.
  private redirectUrl(type: RedirectType): string | null {
    if (
      this.hasMultiplePaymentMethods &&
      (type === 'error' || type === 'declined')
    ) {
      return null
    }

    const url = type === 'success' ? this.successUrl
      : type === 'declined' ? this.declinedUrl
        : type === 'error' ? this.errorUrl : undefined

    if (url && url.startsWith('http')) {
      return url
    }

    if (this.returnUrl && this.returnUrl.startsWith('http')) {
      return this.returnUrl
    }

    return null
  }

  private redirect(type: RedirectType) {
    const url = this.redirectUrl(type)

    if (url) {
      setTimeout(() => {
        this.targetWindow.location.href = url
      }, 3000)
    }
  }

  // The failure message only promises a redirect when one is about to happen.
  private failureMessage(redirectType: RedirectType | null): string {
    return redirectType && this.redirectUrl(redirectType)
      ? 'message.paymentFailedRedirect'
      : 'message.paymentFailed'
  }

  private handlePaymentStatus(
    response: { paymentToken: string },
    redirectForCheck: boolean,
    paymentWithCode: boolean,
    paymentMethod?: PaymentMode
  ) {
    if (redirectForCheck) {
      this.setFeedback({
        type: FeedbackType.success,
        message: 'message.paymentRedirectForCheck',
        step: Step.cardForm,
      }, paymentMethod)

      this.redirect('legacy')
      return
    } else if (!paymentWithCode) {
      this.setFeedback({
        type: FeedbackType.loading,
        message: 'message.paymentCardIsProcessing',
        step: Step.cardForm,
      }, paymentMethod)
    }

    const interval = setInterval(async () => {
      const status = await checkoutService.transactionStatus(response.paymentToken)

      if (status === PaymentStatus.paid || status === PaymentStatus.preAuthorized) {
        clearInterval(interval)

        this.setIsLoading(false, paymentMethod)

        if (paymentWithCode) {
          this.setFeedback({
            type: FeedbackType.success,
            message: 'message.paymentSuccess',
            step: Step.infoForm,
          }, paymentMethod)
        } else {
          this.setFeedback({
            type: FeedbackType.success,
            message: 'message.paymentSuccess',
            step: Step.cardForm,
          }, paymentMethod)
        }

        this.redirect('success')
      } else if (status && status !== PaymentStatus.processing && status !== PaymentStatus.created) {
        clearInterval(interval)

        this.setIsLoading(false, paymentMethod)

        const redirectType = failureRedirect(status)

        if (paymentWithCode) {
          this.setFeedback({
            type: FeedbackType.error,
            message: this.failureMessage(redirectType),
            step: Step.infoForm,
            retry: this.hasMultiplePaymentMethods,
          }, paymentMethod)
        } else {
          this.setFeedback({
            type: FeedbackType.error,
            message: this.failureMessage(redirectType),
            step: Step.cardForm,
            retry: this.hasMultiplePaymentMethods,
          }, paymentMethod)
        }

        if (redirectType) this.redirect(redirectType)
      }
    }, 2000)
  }

  private handleSubscriptionStatus(response: { subscriptionId: string }) {
    const isSubscribingWithCard = this.baseConfig?.subscriptionScheme === SubscriptionScheme.card

    if (isSubscribingWithCard) {
      this.setFeedback({
        type: FeedbackType.loading,
        message: '',
        step: Step.cardForm,
      })
    }

    const interval = setInterval(async () => {
      const status = await checkoutService.subscriptionStatus(response.subscriptionId)

      if (status === SubscriptionStatus.active) {
        clearInterval(interval)

        this.setIsLoading(false)

        this.setFeedback({
          type: FeedbackType.success,
          title: 'message.subscriptionSuccess',
          message: isSubscribingWithCard
            ? 'message.cardSubscriptionSuccessMessage'
            : 'message.subscriptionSuccessMessage',
          step: Step.cardForm,
        })

        this.redirect('success')
      } else if (status && status !== SubscriptionStatus.pending && status !== SubscriptionStatus.created) {
        clearInterval(interval)

        this.setIsLoading(false)

        this.setFeedback({
          type: FeedbackType.error,
          title: 'message.subscriptionFailed',
          message: '',
          step: Step.cardForm,
          retry: false
        })

        if (status === SubscriptionStatus.declined) this.redirect('declined')
        else if (status === SubscriptionStatus.error) this.redirect('error')
      }
    }, 2000)
  }

  async submitCheckoutForm(data: CheckoutForm) {
    const paymentMethod = data.payment.method
    this.setIsLoading(true, paymentMethod)

    try {
      if (
        this.use3ds &&
        this.isSubscribingWithCard &&
        !this.baseConfig?.subscriptionInitialCharge
      ) {
        throw new Error('Card subscription with 3DS authentication requires a initial charge.')
      }

      const response = await checkoutService.post(data, this.nethoneAttemptReference, this.useExternal3ds)

      if (response.bankSlip) {
        this.bankSlip = response.bankSlip
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.pix) {
        this.pix = response.pix
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.pixAutomatico) {
        this.pixAutomatico = response.pixAutomatico
        this.handleSubscriptionStatus(response)
      } else if (response.oxxo) {
        this.oxxo = response.oxxo
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.paynet) {
        this.paynet = response.paynet
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.tiendasYFarmacias) {
        this.tiendasYFarmacias = response.tiendasYFarmacias
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.spei) {
        this.spei = response.spei
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.paycash) {
        this.paycash = response.paycash
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.picpay) {
        this.picpay = response.picpay
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.bankTransfer) {
        this.bankTransfer = response.bankTransfer
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.pagoEfectivo) {
        this.pagoEfectivo = response.pagoEfectivo
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.deuna) {
        this.deuna = response.deuna
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.nequi) {
        this.nequi = response.nequi
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.mach) {
        this.mach = response.mach
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.qrInteroperable) {
        this.qrInteroperable = response.qrInteroperable
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.breb) {
        this.breb = response.breb
        this.handlePaymentStatus(response, false, true, paymentMethod)
      } else if (response.subscriptionId) {
        this.handleSubscriptionStatus(response)
      } else {
        this.handlePaymentStatus(response, !this.processPayment, false, paymentMethod)
      }

      this.sendMessage(MessageType.checkoutSuccess, response)
    } catch (e) {
      const errorMapped = mapError(e as never)
      let title

      if (data.subscription) {
        title = 'message.subscriptionError'
      } else if (data.payment.method === PaymentMode.bankSlip) {
        title = 'message.bankSlipError'
      } else if (data.payment.method === PaymentMode.creditCard || data.payment.method === PaymentMode.debitCard) {
        title = 'message.cardError'
      } else {
        title = 'message.paymentError'
      }

      this.setFeedback({
        title,
        type: FeedbackType.error,
        message: errorMapped.message,
        step: errorMapped.step,
        retry: this.hasMultiplePaymentMethods,
      }, paymentMethod)

      this.sendMessage(MessageType.checkoutError, e)

      this.redirect(errorMapped.code.startsWith('01.') ? 'declined' : 'error')
    } finally {
      this.setIsLoading(false, paymentMethod)
    }
  }

  retry(step?: Step) {
    this.setFeedback(null, this.paymentMethod)

    if (this.pixDirect && !this.pix) {
      this.submitCheckout(this.formData!)
    } else {
      this.$nextTick(() => {
        // No form while a kept result renders instead.
        if (!this.$refs.form) {
          return
        }

        const { steps, goToStep } = this.$refs.form
        const lastStep = Array.from(steps.values())[steps.size - 1]

        if (step !== undefined && steps.has(step)) {
          goToStep(step)
        } else {
          goToStep(lastStep.step)
        }
      })
    }
  }

  async setup3ds(config: CheckoutConfig) {
    if (!config.lightboxUse3ds) return

    try {
      await loadScript(process.env.VUE_APP_BP_MPI_SRC)

      let env = 'HML'

      if (process.env.NODE_ENV === 'production') {
        env = 'PROD'
      } else if (process.env.NODE_ENV === 'sandbox') {
        env = 'SANDBOX'
      }

      ThreeDS.setUp({
        contractId: config.contractId,
        env,
      })

      this.sendMessage(MessageType.emv3dsReady)
    } catch (e) { /** */ }
  }

  async authenticate() {
    const paymentMethod = this.isSubscribingWithCard ? undefined : this.formData!.payment.method
    this.setIsLoading(true, paymentMethod)

    const request = this.formData!.to3ds(this.nethoneAttemptReference)

    if (process.env.VUE_APP_GOOGLE_ANALYTICS_ID) {
      this.$gtag.event('pay', this.formData!.toAnalyticsEvent(true))
    }

    try {
      await ThreeDS.authenticate3DS(request, {
        onSuccess: async (e) => {
          console.info('success', e)

          this.setIsLoading(false, paymentMethod)

          if (e.subscriptionId) {
            this.handleSubscriptionStatus({ subscriptionId: e.subscriptionId! })
          } else {
            this.handlePaymentStatus(e, !this.processPayment, false, paymentMethod)
          }

          if (e.id) {
            // 3DS success event.
            this.sendMessage(MessageType.emv3dsSuccess, e)
          } else {
            delete e.id
            this.sendMessage(MessageType.checkoutSuccess, e)
          }
        },
        onUnenrolled: () => {
          console.error('unenrolled')

          this.setFeedback({
            type: FeedbackType.error,
            message: this.failureMessage('declined'),
            step: Step.cardForm,
            retry: true,
          }, paymentMethod)

          this.setIsLoading(false, paymentMethod)

          this.sendMessage(MessageType.emv3dsUnenrolled)

          this.redirect('declined')
        },
        onFailure: () => {
          console.error('failure')

          this.setFeedback({
            type: FeedbackType.error,
            message: this.failureMessage('error'),
            step: Step.cardForm,
            retry: this.hasMultiplePaymentMethods,
          }, paymentMethod)

          this.setIsLoading(false, paymentMethod)

          this.sendMessage(MessageType.emv3dsFailure)

          this.redirect('error')
        },
        onError: (e) => {
          console.error('error', e)

          this.setFeedback({
            type: FeedbackType.error,
            message: this.failureMessage('error'),
            step: Step.cardForm,
            retry: this.hasMultiplePaymentMethods,
          }, paymentMethod)

          this.setIsLoading(false, paymentMethod)

          this.sendMessage(MessageType.emv3dsError, e)

          this.redirect('error')
        },
      })
    } catch (e) {
      this.setFeedback({
        type: FeedbackType.error,
        title: 'message.systemError',
        message: 'message.checkoutLoadFailed',
      }, paymentMethod)

      this.setIsLoading(false, paymentMethod)

      this.sendMessage(MessageType.emv3dsError, e)

      this.redirect('error')
    }
  }

  private initializeSizeObserver(): void {
    const targetOrigin = this.targetOrigin
    const targetWindow = this.targetWindow

    if (!targetOrigin || !targetWindow) {
      return
    }

    this.sizeObserverInstance = new SizeObserver({
      targetOrigin,
      targetWindow,
    })

    this.sizeObserverInstance.start()
  }

  private cleanupSizeObserver(): void {
    if (this.sizeObserverInstance) {
      this.sizeObserverInstance.stop()
      this.sizeObserverInstance = null
    }
  }

  private setIsLoading(value: boolean, mode?: PaymentMode | null) {
    this.loadingMap = {
      ...this.loadingMap,
      [mode ?? 'checkout']: value,
    }
  }

  private setFeedback(feedback: Feedback | null, mode?: PaymentMode | null) {
    this.feedbackMap = {
      ...this.feedbackMap,
      [mode ?? 'checkout']: feedback,
    }
  }
}
